/**
 * The People view's feeds, straight from pv-auth (2026-09-18):
 *   GET  /provider/admin/people              one row per person, filterable, sortable, paged
 *   GET  /provider/admin/people/funnel       drop-off per step for 7 d and 30 d, top three stopped-at steps
 *   GET  /provider/admin/people/needs        everything people typed, with a suggested tag
 *   GET  /provider/admin/people/assignees    who a re-engage task can go to
 *   GET  /provider/admin/people/:id          the whole journey of one person
 *   POST /provider/admin/people/:id/reengage a GHL task + a journal note (sends nothing to the person)
 *   POST /provider/admin/people/:id/message  a real email or SMS to the person, optionally in reply to one of theirs (#332, #336)
 *   POST /provider/admin/people/:id/inbound/:messageId/read   a human read one of their messages (#336)
 * Shapes mirror src/services/adminPeople.service.ts and adminPeople.pure.ts in
 * pv-auth-service. Nothing here is computed from a guess: a null is a thing
 * the server could not measure and the page prints the words for it.
 */

import { localDayKey } from './format';

export const STAGES = ['signed_up', 'verified', 'first_check', 'day_2', 'day_3', 'one_time_look', 'trial', 'paid'] as const;
export type Stage = (typeof STAGES)[number];
export const STAGE_LABEL: Record<Stage, string> = {
  signed_up: 'Signed up',
  verified: 'Verified',
  first_check: 'First check',
  day_2: 'Day 2',
  day_3: 'Day 3',
  one_time_look: 'One-time look',
  trial: 'Trial',
  paid: 'Paid',
};

export const PLANS = ['free', 'trial', 'paid_monthly', 'paid_annual', 'paid_other', 'comp'] as const;
export type Plan = (typeof PLANS)[number];
export const PLAN_LABEL: Record<Plan, string> = { free: 'Free', trial: 'Trial', paid_monthly: 'Paid monthly', paid_annual: 'Paid annual', paid_other: 'Paid', comp: 'Comp' };

export const PLATFORMS = ['web', 'mac', 'windows', 'ios', 'android'] as const;
export type Platform = (typeof PLATFORMS)[number];
export const PLATFORM_LABEL: Record<Platform, string> = { web: 'Web', mac: 'Mac', windows: 'Windows', ios: 'iOS', android: 'Android' };

export const LAST_SEEN_BUCKETS = ['today', '3d', '7d', '30d', 'older', 'never'] as const;
export type LastSeenBucket = (typeof LAST_SEEN_BUCKETS)[number];
export const LAST_SEEN_LABEL: Record<LastSeenBucket, string> = { today: 'Today', '3d': 'Last 3 days', '7d': 'Last 7 days', '30d': 'Last 30 days', older: 'Older', never: 'Never seen' };

export const DOOR_LABEL: Record<string, string> = { office: 'Office', fitness: 'Fitness', yoga: 'Yoga and breath', biohacker: 'Biohacker', tech: 'Healthier with tech' };

export const SORT_KEYS = ['last_seen', 'stage', 'checks', 'signup'] as const;
export type SortKey = (typeof SORT_KEYS)[number];
export const SORT_LABEL: Record<SortKey, string> = { last_seen: 'Last seen', stage: 'Stage', checks: 'Checks', signup: 'Signed up' };

export const NEED_TAGS = ['bug', 'request', 'confusion', 'praise'] as const;
export type NeedTag = (typeof NEED_TAGS)[number];
export const NEED_LABEL: Record<NeedTag | 'untagged', string> = { bug: 'Bug', request: 'Request', confusion: 'Confusion', praise: 'Praise', untagged: 'Untagged' };

/* ── Where they stopped, why (pv-auth adminPeople.stuck.ts, firstCheckAttempt.service.ts, PR #321 and #322) ── */

/** The seven milestones, in order. The person page draws the four leading ones as dots. */
export const MILESTONES = ['verified', 'first_check', 'reminder_set', 'second_day_check', 'active_or_calendar', 'converted', 'paid'] as const;
export type Milestone = (typeof MILESTONES)[number];
export const MILESTONE_LABEL: Record<Milestone, string> = {
  verified: 'Verified',
  first_check: 'First check',
  reminder_set: 'Reminder set',
  second_day_check: 'Second-day check',
  active_or_calendar: 'Active Mode or calendar',
  converted: 'Converted',
  paid: 'Paid',
};
/** The four the person page draws as dots (ACTIVATION_FUNNEL_V1 section 1, the leading milestones). */
export const DOT_MILESTONES = ['first_check', 'reminder_set', 'second_day_check', 'active_or_calendar'] as const satisfies readonly Milestone[];
export type Milestones = Record<Milestone, string | null>;

/** The stuck reasons pv-auth names, word for word from adminPeople.stuck.ts STUCK_REASONS. */
export const STUCK_REASONS = [
  'never_opened_check',
  'never_started_camera',
  'camera_permission_denied',
  'camera_gate_failed',
  'connection_failed',
  'settle_stall',
  'abandoned_mid_beats',
  'verification_expired',
  'finished_no_baseline',
  'phone_moved',
  'unknown',
] as const;
export type StuckReason = (typeof STUCK_REASONS)[number];
export const STUCK_REASON_LABEL: Record<StuckReason, string> = {
  never_opened_check: 'never opened the check',
  never_started_camera: 'opened the check, never started the camera',
  camera_permission_denied: 'camera permission denied',
  camera_gate_failed: 'camera gate never passed',
  connection_failed: 'media connection failed',
  settle_stall: 'beat-1 front hold never completed',
  abandoned_mid_beats: 'left mid-check',
  verification_expired: 'verification link expired',
  finished_no_baseline: 'finished, no baseline stored',
  phone_moved: 'phone moved mid-check',
  unknown: 'not measurable',
};
export type StuckSource = 'posthog' | 'db' | 'not_fetched' | 'unavailable';

export const ATTEMPT_OUTCOMES = ['started', 'success', 'failed'] as const;
export type AttemptOutcome = (typeof ATTEMPT_OUTCOMES)[number];
export const ATTEMPT_REASONS = ['too_dark', 'too_close', 'camera_gate_failed', 'camera_permission_denied', 'camera_error', 'connection_failed', 'settle_stall', 'phone_moved', 'left', 'unknown'] as const;
export type AttemptReason = (typeof ATTEMPT_REASONS)[number];
export const ATTEMPT_REASON_LABEL: Record<AttemptReason, string> = {
  too_dark: 'too dark',
  too_close: 'too close to the camera',
  camera_gate_failed: 'camera gate never passed',
  camera_permission_denied: 'camera permission denied',
  camera_error: 'camera error',
  connection_failed: 'connection failed',
  settle_stall: 'front hold never settled',
  phone_moved: 'phone moved',
  left: 'left mid-check',
  unknown: 'not measurable',
};
/** The check surfaces the clients name (frontend first-check-telemetry.ts CheckSurface, plus the apps). */
export const ATTEMPT_PLATFORM_LABEL: Record<string, string> = {
  web_iphone: 'iPhone browser',
  web_ipad: 'iPad browser',
  web_android: 'Android browser',
  web_mac: 'Mac browser',
  web_windows: 'Windows browser',
  web_other: 'browser',
  desktop_app: 'desktop app',
  ios: 'iOS app',
  android: 'Android app',
};

/** One attempt as the API reports it: the row's own fields plus how long ago. */
export interface AttemptView {
  id: string;
  attemptN: number;
  outcome: AttemptOutcome;
  reason: AttemptReason | null;
  reasonLabel: string | null;
  stage: string | null;
  platform: string | null;
  seconds: number | null;
  at: string;
  minutesAgo: number;
}
/** The compact summary on a People row. Counts are counts of rows; nulls mean no such row. */
export interface AttemptsSummary {
  count: number;
  failed: number;
  succeeded: number;
  lastOutcome: AttemptOutcome | null;
  lastReason: AttemptReason | null;
  lastReasonLabel: string | null;
  lastPlatform: string | null;
  lastAt: string | null;
  openFailure: AttemptView | null;
}
export const EMPTY_ATTEMPTS: AttemptsSummary = { count: 0, failed: 0, succeeded: 0, lastOutcome: null, lastReason: null, lastReasonLabel: null, lastPlatform: null, lastAt: null, openFailure: null };

/** Feel tags (ACTIVE_MODE_DAY_INSIGHT_V1 section 2): how many, the last word, tags per session (null with no session). */
export interface FeelTagsSummary { count: number; lastTag: string | null; lastAt: string | null; ratePerSession: number | null }
export interface FeelTagView { at: string; tag: string; sessionId: string; minuteInSession: number | null; moment: string | null; run: number | null; inBusyBlock: boolean }

export const KNOWLEDGE_LABEL: Record<string, string> = {
  goal: 'Goal',
  door: 'Door',
  work_setting: 'Work setting',
  screen_hours: 'Screen hours',
  hard_time: 'When it gets hard',
  symptoms: 'What they notice',
  encouragement_style: 'Encouragement',
  check_in_time: 'Check-in time',
  preferred_practice: 'Preferred practice',
  assessment_cadence: 'Assessment cadence',
  daily_movement_willingness: 'Daily movement',
  discomfort_where: 'Discomfort where',
  discomfort_level: 'Discomfort level',
  worst_time: 'Worst time',
  what_helps: 'What helps',
  stress_level: 'Stress level',
};

export const SOURCE_LABEL: Record<string, string> = {
  feedback: 'Something wrong',
  help_request: 'Help request',
  ticket: 'Support ticket',
  exercise_request: 'Exercise request',
  reflection: 'Reflection',
  elina_converse: 'Said to Elina',
  elina_chat: 'Elina chat',
  elina_answer: 'Answer to Elina',
  customize: 'Customize answer',
  post_check: 'After a check',
  cancellation: 'Cancellation',
  exercise_note: 'Exercise note',
  seventh_check_answer: 'Seventh check answer',
  improve: 'What to improve',
  evening_note: 'Evening note',
  inbound_email: 'Email from them',
  inbound_sms: 'SMS from them',
  inbound_unmatched: 'Message, no account',
  pain_stop: 'Said it hurt, stopped',
};

/* ── "Stop, this hurts" (pv-auth #337 PainEvent) ────────────────────────── */

/** How many times Elina heard "stop, this hurts" from this person, and when the latest was. The flagged lines are on the detail. */
export interface PainEventsSummary { count: number; latestAt: string | null }
export const EMPTY_PAIN_EVENTS: PainEventsSummary = { count: 0, latestAt: null };
export const PAIN_ACTIVITY_KINDS = ['activation', 'rom', 'daily_check', 'active_mode', 'conversation'] as const;
export type PainActivityKind = (typeof PAIN_ACTIVITY_KINDS)[number];
export const PAIN_ACTIVITY_LABEL: Record<PainActivityKind, string> = { activation: 'an activation', rom: 'a range-of-motion movement', daily_check: 'a daily check', active_mode: 'Active Mode', conversation: 'a conversation with Elina' };
export type PainSource = 'coach' | 'converse' | 'client';
export const PAIN_SOURCE_LABEL: Record<PainSource, string> = { coach: 'heard by the coach', converse: 'heard in the Pulse console', client: 'recorded by the client' };
/**
 * One "stop, this hurts", as the API carries it (snake_case on the wire,
 * painEvent.service.ts PainEventView). Never a diagnosis: what was said,
 * what they were doing, and when. `painEventFields` reads either spelling.
 */
export interface PainEventView {
  id: string;
  activity_kind: PainActivityKind;
  activity_id: string | null;
  phase: string | null;
  side: 'left' | 'right' | null;
  elapsed_seconds: number | null;
  /** Null on a red flag: the words were not kept (pv-auth #480, invariant 88); `row` says which. */
  transcript: string | null;
  /** The safety matrix row the words matched (E1 to E4, R1, R2, R4, R5), else null. */
  row?: string | null;
  source: PainSource;
  at: string;
}

/** What the page shows where a red flag's words were not kept. Never blank, never the words. */
export const painWordsNotKept = (row: string | null): string => `Words not kept: red flag${row ? ` ${row}` : ''}.`;
/** A pain event as the wire may spell it: the API's snake_case view, or the camelCase form. */
export type PainEventLike = PainEventView | Record<string, unknown>;
/** The event's fields in one shape, whichever spelling the wire used. */
export const painEventFields = (raw: PainEventLike): { id: string; kind: string; activityId: string | null; phase: string | null; side: string | null; elapsedSeconds: number | null; transcript: string; row: string | null; wordsKept: boolean; source: string; at: string } => {
  const e = raw as Record<string, unknown>;
  const str = (v: unknown): string | null => (typeof v === 'string' && v ? v : null);
  const n = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null);
  return {
    id: str(e.id) ?? '',
    kind: str(e.activity_kind) ?? str(e.activityKind) ?? 'unknown',
    activityId: str(e.activity_id) ?? str(e.activityId),
    phase: str(e.phase),
    side: str(e.side),
    elapsedSeconds: n(e.elapsed_seconds) ?? n(e.elapsedSeconds),
    transcript: typeof e.transcript === 'string' ? e.transcript : painWordsNotKept(str(e.row)),
    row: str(e.row),
    wordsKept: typeof e.transcript === 'string',
    source: str(e.source) ?? 'unknown',
    at: str(e.at) ?? '',
  };
};
/** "during an activation, phase 2, left side, 40 s in": what they were doing when they said it. */
export const painWhere = (e: PainEventLike): string => {
  const f = painEventFields(e);
  const parts = [`during ${(PAIN_ACTIVITY_LABEL as Record<string, string>)[f.kind] ?? f.kind.replace(/_/g, ' ')}`];
  if (f.activityId) parts.push(f.activityId);
  if (f.phase) parts.push(`phase ${f.phase}`);
  if (f.side) parts.push(`${f.side} side`);
  if (f.elapsedSeconds !== null) parts.push(`${f.elapsedSeconds} s in`);
  return parts.join(', ');
};
/** "2 pain stops" for the row chip; empty at zero. */
export const painChipText = (s: PainEventsSummary | null | undefined): string => (!s || s.count === 0 ? '' : `${s.count} pain ${s.count === 1 ? 'stop' : 'stops'}`);

/* ── Variable readings (pv-auth #452 src/elina/movementNormal.ts) ── */

export interface VariableMovement { movementId: string; normalDeg: number; spreadDeg: number; thresholdDeg: number; last: number[] }
export interface VariableReadingsSummary { flaggedAt: string; lastAlertAt: string | null; movements: VariableMovement[] }
const MOVEMENT_NAME: Record<string, string> = {
  rotation_left: 'turn left',
  rotation_right: 'turn right',
  lateral_bend_left: 'tilt left',
  lateral_bend_right: 'tilt right',
  flexion: 'chin down',
  extension: 'chin up',
};
export const movementName = (id: string): string => MOVEMENT_NAME[id] ?? id.replace(/_/g, ' ');
/** The row chip: which movements, nothing when not flagged. */
export const variableChipText = (v: VariableReadingsSummary | null | undefined): string =>
  !v || v.movements.length === 0 ? '' : `variable readings: ${v.movements.map((m) => movementName(m.movementId)).join(', ')}`;
/** One movement with every number: the readings newest first, the normal, the spread against its line. */
export const variableMovementLine = (m: VariableMovement): string =>
  `${movementName(m.movementId)}: ${m.last.join(', ')} degrees, newest first · normal ${Math.round(m.normalDeg)} · spread ${m.spreadDeg} (line ${m.thresholdDeg})`;

/* ── What they wrote us, what they told us (pv-auth #336 inbound, #334 feedback, #326 evening notes) ── */

/** What they sent us through GHL: how many, how many nobody has read, when the latest arrived. The words are on the detail only. */
export interface InboundSummary { count: number; unread: number; latestAt: string | null }
export const EMPTY_INBOUND: InboundSummary = { count: 0, unread: 0, latestAt: null };
export type InboundChannel = 'email' | 'sms' | 'other';
export const INBOUND_CHANNEL_LABEL: Record<InboundChannel, string> = { email: 'Email', sms: 'SMS', other: 'Message' };
/** One message they sent us, in full, as GET /provider/admin/people/:id carries it. */
export interface InboundMessageView {
  id: string;
  ghlMessageId: string;
  ghlConversationId: string;
  ghlContactId: string;
  channel: InboundChannel;
  subject: string | null;
  body: string;
  receivedAt: string;
  /** Null until a human reads it or replies; that is what "unread" means. */
  readAt: string | null;
  readBy: string | null;
  /** The thread in GHL, null when the location is not configured. */
  ghlUrl: string | null;
}

/** What they told us about the product (UserFeedback): counts for the row; the words are on the detail. */
export interface FeedbackSummary { count: number; latestAt: string | null; unreadRecordings: number }
export const EMPTY_FEEDBACK: FeedbackSummary = { count: 0, latestAt: null, unreadRecordings: 0 };
export interface EveningNoteSummary { count: number; latestAt: string | null }
export const EMPTY_EVENING_NOTE: EveningNoteSummary = { count: 0, latestAt: null };
export type FeedbackKind = 'seventh_check' | 'improve';
export const FEEDBACK_KIND_LABEL: Record<FeedbackKind, string> = { seventh_check: 'Seventh check question', improve: 'What to improve' };
export type SaidSource = 'voice' | 'text';
export const SAID_LABEL: Record<SaidSource, string> = { voice: 'said', text: 'typed' };
/** The server's own line for a recording nobody could transcribe; the words are never blank. */
export const UNREAD_RECORDING_LINE = 'A recording is waiting to be read: we could not transcribe it.';
/** One thing they told us about the product, in full. `transcript` null is a recording still waiting to be read, never empty words. */
export interface ProductFeedbackView {
  at: string;
  kind: FeedbackKind;
  source: SaidSource;
  transcript: string | null;
  hasAudio: boolean;
  audioSeconds: number | null;
  context: Record<string, unknown> | null;
  /** The row's id, readAt and readBy (pv-auth #337): the read mark names the id. Absent until #337 is deployed; no Mark read without an id. */
  id?: string;
  readAt?: string | null;
  readBy?: string | null;
}
/** One evening voice note in full (DayNote). `id`, `readAt`, `readBy` as on feedback (pv-auth #337). */
export interface EveningNoteView { at: string; day: string; timeZone: string; source: SaidSource; audioSeconds: number | null; transcript: string; id?: string; readAt?: string | null; readBy?: string | null }
/** The answer of POST /provider/admin/people/:id/feedback/:feedbackId/read: which row, which table, read when and by whom. */
export interface ReadMark { id: string; kind: 'product_feedback' | 'evening_note'; readAt: string; readBy: string }

export interface PersonSource {
  promoterCode: string | null;
  utmSource: string | null;
  utmMedium: string | null;
  utmCampaign: string | null;
  referredBy: { userId: string; email: string } | null;
  door: string | null;
  label: string;
}

export interface PersonRow {
  userId: string;
  email: string;
  name: string;
  firstName: string;
  phone: string | null;
  timezone: string | null;
  signupAt: string;
  verified: boolean;
  qa: boolean;
  source: PersonSource;
  plan: Plan;
  planDetail: { planName: string | null; interval: string | null; status: string | null; store: string | null; trialEndsAt: string | null };
  stage: Stage;
  stageLabel: string;
  lastSeenAt: string | null;
  lastSeenSource: string | null;
  daysSinceSeen: number | null;
  lastSeenBucket: LastSeenBucket;
  streak: number | null;
  checks: number;
  checkDays: number;
  firstCheckAt: string | null;
  lastCheckAt: string | null;
  lastScore: number | null;
  activeMinutes7d: number | null;
  activeMinutesTotal: number | null;
  activeSessions: number;
  platforms: Platform[];
  elina: Record<string, string>;
  openTickets: number;
  /** Help requests from the in-app Help form (pv-auth, 2026-10-21): how many, how many still open, the latest. Absent on an older server. */
  helpRequests?: { total: number; open: number; lastAt: string | null };
  feedbackCount: number;
  exerciseRequests: number;
  ghlContactId: string | null;
  ghlUrl: string | null;
  lastEventKind: string | null;
  reached: { step: Stage; at: string | null }[];
  feelTags: FeelTagsSummary;
  /** The seven milestones, each an ISO instant off a row or null when not reached. */
  milestones: Milestones;
  /** False when the account is verified but the click is not on record; `milestones.verified` is then the signup instant. */
  verifiedAtRecorded: boolean;
  /** The first milestone not reached, from the database; null when all seven are. */
  stuckStep: Milestone | null;
  stuckStepLabel: string | null;
  /** Why they stopped. Null until fetched for this row (the list fetches for its page, the detail for its person) or when not measurable; `stuckSource` says which. */
  stuckReason: StuckReason | null;
  stuckReasonLabel: string | null;
  stuckDetail: Record<string, unknown> | null;
  stuckBasis: { event: string; at: string } | null;
  lastEventAt: string | null;
  stuckSource: StuckSource;
  stuckUnavailableReason: string | null;
  verificationLinkExpiredAt: string | null;
  /** First-check attempts the client reported (FirstCheckAttempt, rule 5). */
  attempts: AttemptsSummary;
  /** How many evening voice notes, and when the latest was (pv-auth #329). Words are on the detail. */
  eveningNote: EveningNoteSummary;
  /** How many things they told us about the product, and when (pv-auth #334). Words are on the detail. */
  productFeedback: FeedbackSummary;
  /** What they sent us through GHL: count, unread, latest (pv-auth #336). Words are on the detail. */
  inbound: InboundSummary;
  /** "Stop, this hurts": how many and when (pv-auth #337). Absent until deployed. The flagged lines are on the detail. */
  painEvents?: PainEventsSummary;
  /**
   * "Variable readings" (pv-auth #452, Brian 2026-10-21): the movements whose
   * last five readings spread past their line, with the numbers. Null when not
   * flagged or settled since; absent until #452 is deployed.
   */
  variableReadings?: VariableReadingsSummary | null;
}

export interface PeopleQuery {
  stage?: Stage | null;
  plan?: Plan | null;
  platform?: Platform | null;
  lastSeen?: LastSeenBucket | null;
  door?: string | null;
  q?: string | null;
  /**
   * A stuck reason. The server has no such filter and attaches the reason
   * only to the page it returns, so this one is applied in the browser to
   * the rows on the page; it lives in the URL like the others so a funnel
   * row can link to it.
   */
  stuck?: StuckReason | null;
  sort?: SortKey;
  dir?: 'asc' | 'desc';
  page?: number;
  pageSize?: number;
  includeQa?: boolean;
}

export interface PeopleList {
  generatedAt: string;
  total: number;
  totalAll: number;
  page: number;
  pageSize: number;
  sort: { key: SortKey; dir: 'asc' | 'desc' };
  facets: { stage: Record<string, number>; plan: Record<string, number>; platform: Record<string, number>; lastSeen: Record<string, number>; door: Record<string, number> };
  people: PersonRow[];
  truncated: boolean;
  unavailable: { source: string; reason: string }[];
}

export interface FunnelStepCount { step: Stage; label: string; reached: number; dropped: number | null; droppedPct: number | null }
export interface StoppedStep { step: Stage; label: string; count: number; lastEvent: { kind: string; count: number } | null }
export interface FunnelWindow { days: number; since: string; cohort: number; steps: FunnelStepCount[]; stoppedAt: StoppedStep[] }
export interface StuckReasonCount { reason: StuckReason; label: string; count: number }
export interface MilestoneStepCount { milestone: Milestone; label: string; reached: number; pctOfCohort: number | null; dropped: number | null; medianMinutesFromPrevious: number | null }
/** The seven milestones over a signup window, with why the people short of a first check stopped. */
export interface MilestoneFunnelWindow {
  days: number;
  since: string;
  cohort: number;
  steps: MilestoneStepCount[];
  stoppedBefore: { milestone: Milestone; label: string; count: number }[];
  /** Counts per stuck reason among the cohort's people with no first check; `stuckMeasured` is how many of them got a reason, the rest are `stuckNotMeasured`. */
  stuckReasons: StuckReasonCount[];
  stuckMeasured: number;
  stuckNotMeasured: number;
}
export interface PeopleFunnel { generatedAt: string; windows: FunnelWindow[]; milestones: MilestoneFunnelWindow[]; unavailable: { source: string; reason: string }[] }

export interface TimelineEvent { id: string; at: string; kind: string; summary: string; data: Record<string, unknown> }
export interface ToldUs { at: string; source: string; text: string; tag: NeedTag | null; context: Record<string, unknown> }
export interface StoppedAt { lastCompleted: Stage; lastCompletedAt: string | null; firstUncompleted: Stage | null; daysSince: number | null; line: string }
export interface JournalNote { id: string; body: string; authorEmail: string | null; createdAt: string }
export interface PersonDetail {
  generatedAt: string;
  person: PersonRow;
  stoppedAt: StoppedAt;
  timeline: TimelineEvent[];
  timelineCursor: string | null;
  unavailable: { source: string; reason: string }[];
  toldUs: ToldUs[];
  notes: JournalNote[];
  /** The last five first-check attempts the client reported, newest first. */
  attempts: AttemptView[];
  feelTags: FeelTagView[];
  /** The evening voice notes in full, newest first. */
  eveningNotes: EveningNoteView[];
  /** What they told us about the product, in full, newest first. */
  productFeedback: ProductFeedbackView[];
  /** What they sent us through GHL, in full, newest first; the console puts unread first. Absent until pv-auth #336 is deployed. */
  inbound?: InboundMessageView[];
  /** Every "stop, this hurts" Elina heard, newest first (pv-auth #337). Absent until deployed. */
  painEvents?: PainEventView[];
  emailOpens: null;
  emailOpensReason: string;
  /**
   * The full-body assessments, newest first, each row as the client measured
   * it (pv-auth #442; FULL_BODY_ASSESSMENT_2026-10-18). Absent until deployed.
   * A null value is not measured, never zero.
   */
  fullBody?: FullBodyRowView[];
  /**
   * Awareness checks (pv-auth #474; FUNCTIONAL_EXAMS_2026-10-24): the
   * functional check runs, newest first, dates and means in degrees only.
   * Absent until deployed. A null mean is not measured, never zero.
   */
  awarenessChecks?: AwarenessCheckRow[];
  /**
   * The moment their check belongs to, in their own words (pv-auth #466, #467;
   * FIRST_WEEK_LOOP section 2). Absent until deployed or while
   * FIRST_WEEK_LOOP_V1 is off; null when none is set. Detail only, never on
   * the list row. Every field is optional here: the server may send less.
   */
  moment?: MomentView | null;
}

export interface MomentView {
  /** What they chose or typed, as it reads. */
  words?: string | null;
  /** The enum key (morning_coffee, custom, ...). */
  moment?: string | null;
  event?: string | null;
  device?: string | null;
  setAt?: string | null;
  askedCount?: number | null;
}

export interface FullBodyMeasureView {
  key: string;
  value: number | null;
  unit: 'deg' | 'ratio' | null;
  words: string;
  measured: boolean;
  source: 'client_estimate' | 'engine' | null;
  confidence: 'low' | 'moderate' | null;
  notMeasuredReason: string | null;
}

/** One functional check run on the person page: words-free, dates and means (degrees). */
export interface AwarenessCheckRow {
  localDate: string;
  testKey: string;
  meanErrorDeg: number | null;
  leftMeanDeg: number | null;
  rightMeanDeg: number | null;
  sampleCount: number;
  notMeasuredReason: string | null;
}

export interface FullBodyRowView {
  id: string;
  localDate: string;
  createdAt: string;
  startedAt: string;
  completedAt: string;
  status: 'complete' | 'stopped';
  stoppedAt: string | null;
  stoppedReason: 'painful' | 'member' | 'camera' | null;
  turnConvention: 'left';
  views: Record<'front' | 'sideRight' | 'sideLeft', { capturedAt: string; framing: 'whole_body' | 'partial'; measures: FullBodyMeasureView[] } | null>;
  movements: Array<{ id: string; attempted: boolean; measured: boolean; achievedDeg: number | null; notMeasuredReason: string | null; readFrom: 'head' | 'trunk' | null }>;
  felt: { feel: string; where: string | null; source: 'voice' | 'tap'; at: string } | null;
  summary: { viewsRead: string[]; measured: number; notMeasured: number; movementsMeasured: number; movementsNotMeasured: number };
}

export interface NeedRow {
  at: string;
  source: string;
  userId: string | null;
  email: string | null;
  name: string | null;
  text: string;
  tag: NeedTag | null;
  context: Record<string, unknown>;
  /** Only on rows that can be read and answered (the inbound messages, #336): true until somebody marks it read or replies. */
  unread?: boolean;
  /** The row's own id where a read can be marked (product feedback and evening notes, #337); null for sources with no read state. */
  id?: string | null;
  /** When somebody on our team read it, and who (#337). Null is unread. */
  readAt?: string | null;
  readBy?: string | null;
}
export interface PeopleNeeds { generatedAt: string; days: number; counts: Record<NeedTag | 'untagged', number>; bySource: Record<string, number>; rows: NeedRow[]; unavailable: { source: string; reason: string }[] }

export interface TaskAssignee { id: string; name: string }
export interface Assignees { assignees: TaskAssignee[]; source: 'env' | 'ghl' | 'none'; reason: string | null }
/**
 * What the two person-page buttons actually did, in the response itself
 * (pv-auth #332): `sentToPerson` is the machine answer, `deliverySummary` the
 * sentence for under the button. Re-engage is always false: it is a task for
 * us, it never messages the person.
 */
export interface ReengageResult { ok: boolean; ghlTaskId: string | null; ghlContactId: string | null; ghlUrl: string | null; ghlError: string | null; note: JournalNote; sentToPerson: false; deliverySummary: string }
export const REENGAGE_DELIVERY_SUMMARY = 'A task was created in GHL for a human to follow up. Nothing was sent to this person.';
export interface MessagePersonResult {
  ok: boolean;
  outreachId: string;
  status: 'sent' | 'queued' | 'failed';
  channel: 'email' | 'sms';
  via: 'ghl' | 'mailer' | null;
  error: string | null;
  message: string | null;
  ghlContactId: string | null;
  ghlUrl: string | null;
  /** True unless the send outright failed. */
  sentToPerson: boolean;
  deliverySummary: string;
  note: JournalNote;
  /** The message this answered, when it was a reply (#336); markedRead is false when the send failed. */
  replyTo?: { messageId: string; channel: string; receivedAt: string; markedRead: boolean } | null;
}

/* ── query string ───────────────────────────────────────────────────────── */

/** The list query as the server takes it. Defaults are left out; `stuck` is not sent because the server has no such filter (peopleLinkString carries it). */
export const peopleQueryString = (q: PeopleQuery): string => {
  const p = new URLSearchParams();
  if (q.stage) p.set('stage', q.stage);
  if (q.plan) p.set('plan', q.plan);
  if (q.platform) p.set('platform', q.platform);
  if (q.lastSeen) p.set('lastSeen', q.lastSeen);
  if (q.door) p.set('door', q.door);
  if (q.q && q.q.trim()) p.set('q', q.q.trim());
  if (q.sort && q.sort !== 'last_seen') p.set('sort', q.sort);
  if (q.dir && q.dir !== 'desc') p.set('dir', q.dir);
  if (q.page && q.page > 1) p.set('page', String(q.page));
  if (q.pageSize && q.pageSize !== 50) p.set('pageSize', String(q.pageSize));
  if (q.includeQa) p.set('includeQa', '1');
  return p.toString();
};

/** The same query as the page URL: the server's params plus the browser-side stuck filter, so a filtered view is a shareable link. */
export const peopleLinkString = (q: PeopleQuery): string => {
  const p = new URLSearchParams(peopleQueryString(q));
  if (q.stuck) p.set('stuck', q.stuck);
  return p.toString();
};

const oneOf = <T extends string>(v: string | null, allowed: readonly T[]): T | null => (v && (allowed as readonly string[]).includes(v) ? (v as T) : null);

/** The reverse: URL params back into a query. Unknown values are dropped rather than sent. */
export const peopleQueryFromParams = (params: URLSearchParams): PeopleQuery => {
  const page = Number(params.get('page'));
  const pageSize = Number(params.get('pageSize'));
  return {
    stage: oneOf(params.get('stage'), STAGES),
    plan: oneOf(params.get('plan'), PLANS),
    platform: oneOf(params.get('platform'), PLATFORMS),
    lastSeen: oneOf(params.get('lastSeen'), LAST_SEEN_BUCKETS),
    door: params.get('door') || null,
    q: params.get('q') || null,
    stuck: oneOf(params.get('stuck'), STUCK_REASONS),
    sort: oneOf(params.get('sort'), SORT_KEYS) ?? 'last_seen',
    dir: params.get('dir') === 'asc' ? 'asc' : 'desc',
    page: Number.isFinite(page) && page > 1 ? Math.floor(page) : 1,
    pageSize: Number.isFinite(pageSize) && pageSize > 0 ? Math.floor(pageSize) : 50,
    includeQa: params.get('includeQa') === '1',
  };
};

/* ── fetchers ───────────────────────────────────────────────────────────── */

export async function fetchPeople(q: PeopleQuery): Promise<PeopleList> {
  const { apiGet } = await import('@/services/base');
  const qs = peopleQueryString(q);
  return (await apiGet(`/provider/admin/people${qs ? `?${qs}` : ''}`, true)) as PeopleList;
}

export async function fetchPeopleFunnel(): Promise<PeopleFunnel> {
  const { apiGet } = await import('@/services/base');
  return (await apiGet('/provider/admin/people/funnel', true)) as PeopleFunnel;
}

export async function fetchPeopleNeeds(days = 30): Promise<PeopleNeeds> {
  const { apiGet } = await import('@/services/base');
  return (await apiGet(`/provider/admin/people/needs?days=${days}&limit=1000`, true)) as PeopleNeeds;
}

export async function fetchAssignees(): Promise<Assignees> {
  const { apiGet } = await import('@/services/base');
  return (await apiGet('/provider/admin/people/assignees', true)) as Assignees;
}

export async function fetchPerson(userId: string): Promise<PersonDetail> {
  const { apiGet } = await import('@/services/base');
  return (await apiGet(`/provider/admin/people/${encodeURIComponent(userId)}`, true)) as PersonDetail;
}

export async function reengagePerson(input: { userId: string; assigneeId: string | null; assigneeName: string | null; note: string; dueInDays: number }): Promise<ReengageResult> {
  const { apiPost } = await import('@/services/base');
  const { userId, ...body } = input;
  return (await apiPost(`/provider/admin/people/${encodeURIComponent(userId)}/reengage`, body, true)) as ReengageResult;
}

/**
 * POST /admin/subscriptions/cancel (pv-auth, the admin cancel). The member's
 * own cancel run by an admin: at the end of the period already paid for,
 * idempotent, Stripe's end date. A refusal (store-billed, nothing to cancel)
 * or a Stripe error comes back as a 4xx/5xx with its message, which apiPost
 * throws; a 200 without an end date is not treated as a cancel either.
 */
export interface CancelSubscriptionResult { endsAt: string; alreadyCancelled: boolean; message: string }
export const CANCEL_NOT_CONFIRMED = 'Not confirmed: the server answered without an end date. Check Stripe before telling the member it is cancelled.';
export async function cancelPersonSubscription(userId: string): Promise<CancelSubscriptionResult> {
  const { apiPost } = await import('@/services/base');
  const r = (await apiPost('/admin/subscriptions/cancel', { userId }, true)) as { success?: boolean; message?: string; data?: { endsAt?: unknown; alreadyCancelled?: unknown } } | null;
  const endsAt = r?.data?.endsAt;
  if (!r || r.success !== true || typeof endsAt !== 'string' || Number.isNaN(Date.parse(endsAt))) {
    throw new Error(CANCEL_NOT_CONFIRMED);
  }
  return { endsAt, alreadyCancelled: r.data?.alreadyCancelled === true, message: r.message ?? '' };
}

/**
 * POST /provider/admin/people/:id/message: a real email or SMS to the person
 * (pv-auth #332). With `replyToMessageId` it answers one of their inbound
 * messages: threaded in the journal and marked read on a send that went out
 * (#336). An email reply with no subject typed goes under "Re: theirs".
 */
export async function messagePerson(input: { userId: string; channel: 'email' | 'sms'; subject?: string | null; body: string; replyToMessageId?: string | null }): Promise<MessagePersonResult> {
  const { apiPost } = await import('@/services/base');
  const { userId, replyToMessageId, subject, ...rest } = input;
  const body = { ...rest, ...(subject ? { subject } : {}), ...(replyToMessageId ? { replyToMessageId } : {}) };
  return (await apiPost(`/provider/admin/people/${encodeURIComponent(userId)}/message`, body, true)) as MessagePersonResult;
}

/** POST /provider/admin/people/:id/inbound/:messageId/read: a human read it. Not a reply. Returns the message as it now stands. */
export async function markInboundRead(userId: string, messageId: string): Promise<InboundMessageView> {
  const { apiPost } = await import('@/services/base');
  const r = (await apiPost(`/provider/admin/people/${encodeURIComponent(userId)}/inbound/${encodeURIComponent(messageId)}/read`, {}, true)) as { message: InboundMessageView };
  return r.message;
}

/* ── what they wrote, what they told us: the helpers ────────────────────── */

/** Unread first, then newest first. The server sends newest first; this is the console's own rule so the page leads with what is waiting. */
export const sortInboundUnreadFirst = <T extends { readAt: string | null; receivedAt: string }>(list: readonly T[]): T[] =>
  [...list].sort((a, b) => {
    const ua = a.readAt ? 1 : 0;
    const ub = b.readAt ? 1 : 0;
    if (ua !== ub) return ua - ub;
    return a.receivedAt < b.receivedAt ? 1 : a.receivedAt > b.receivedAt ? -1 : 0;
  });

/**
 * A Needs row counts as read only when the server says so: an inbound
 * message with `unread: false` (#336) or a row with `readAt` set (#337). A
 * row with no read state at all (a source that cannot be marked) is not
 * read: nothing about it says it has been dealt with, so it stays in front.
 */
export const isNeedRead = (r: Pick<NeedRow, 'unread' | 'readAt'>): boolean => r.unread === false || !!r.readAt;

/** The same rule over Needs rows: the ones not read first, then newest first within each half. */
export const sortNeedsUnreadFirst = <T extends Pick<NeedRow, 'unread' | 'readAt' | 'at'>>(rows: readonly T[]): T[] =>
  [...rows].sort((a, b) => {
    const ua = isNeedRead(a) ? 1 : 0;
    const ub = isNeedRead(b) ? 1 : 0;
    if (ua !== ub) return ua - ub;
    return a.at < b.at ? 1 : a.at > b.at ? -1 : 0;
  });

/** A Needs row whose read can be marked through the feedback endpoint: product feedback or an evening note with an id (#337). */
export const TOLD_US_SOURCES = ['seventh_check_answer', 'improve', 'evening_note'] as const;
export const isToldUsNeed = (r: Pick<NeedRow, 'source' | 'userId' | 'id'>): boolean => (TOLD_US_SOURCES as readonly string[]).includes(r.source) && !!r.userId && !!r.id;

/** POST /provider/admin/people/:id/feedback/:feedbackId/read (pv-auth #337): a human read one thing they told us, a feedback row or an evening note, whichever the id names. */
export async function markToldUsRead(userId: string, itemId: string): Promise<ReadMark> {
  const { apiPost } = await import('@/services/base');
  return (await apiPost(`/provider/admin/people/${encodeURIComponent(userId)}/feedback/${encodeURIComponent(itemId)}/read`, {}, true)) as ReadMark;
}

/** Unread first, then newest first, for the feedback and note views (a view with no id has no read state and stays in front). */
export const sortToldUsUnreadFirst = <T extends { readAt?: string | null; at: string }>(list: readonly T[]): T[] =>
  [...list].sort((a, b) => {
    const ua = a.readAt ? 1 : 0;
    const ub = b.readAt ? 1 : 0;
    if (ua !== ub) return ua - ub;
    return a.at < b.at ? 1 : a.at > b.at ? -1 : 0;
  });

/** A Needs row that is one of their inbound messages on an account: it can be read and replied to. */
export const isInboundNeed = (r: Pick<NeedRow, 'source' | 'userId'>): boolean => (r.source === 'inbound_email' || r.source === 'inbound_sms') && !!r.userId;
/** A Needs row from a GHL contact with no account: a link to the thread, nothing to reply to here. */
export const isUnmatchedNeed = (r: Pick<NeedRow, 'source'>): boolean => r.source === 'inbound_unmatched';

/** "Re: their subject" for an email reply; theirs as it is when it already says Re:. Empty with no subject. */
export const replySubject = (subject: string | null | undefined): string => {
  const s = (subject ?? '').trim();
  if (!s) return '';
  return /^re:/i.test(s) ? s : `Re: ${s}`;
};

/** The words for a piece of product feedback: the transcript, or the server's line for a recording nobody could read. */
export const feedbackWords = (f: Pick<ProductFeedbackView, 'transcript'>): { text: string; transcribed: boolean } =>
  f.transcript && f.transcript.trim() ? { text: f.transcript.trim(), transcribed: true } : { text: UNREAD_RECORDING_LINE, transcribed: false };

/** "2 unread of 5" or "3" for the row chip; empty at zero, so the chip can hide. */
export const inboundChipText = (s: InboundSummary | null | undefined): string => {
  if (!s || s.count === 0) return '';
  return s.unread > 0 ? `${s.unread} unread of ${s.count}` : `${s.count} read`;
};

/** "3 told us" and "2 evening notes" as separate chips; each empty at zero. */
export const feedbackChipText = (s: FeedbackSummary | null | undefined): string => {
  if (!s || s.count === 0) return '';
  return `${s.count} told us${s.unreadRecordings > 0 ? `, ${s.unreadRecordings} to listen to` : ''}`;
};
export const eveningChipText = (s: EveningNoteSummary | null | undefined): string => (!s || s.count === 0 ? '' : `${s.count} evening ${s.count === 1 ? 'note' : 'notes'}`);

/* ── small helpers for the pages ────────────────────────────────────────── */

/** The event kind the funnel names, as words. */
export const eventKindLabel = (kind: string | null): string => {
  if (!kind) return 'nothing recorded';
  if (kind.startsWith('email:')) return `email: ${kind.slice(6).toLowerCase().replace(/_/g, ' ')}`;
  const map: Record<string, string> = {
    signup: 'signup only',
    login: 'signed in',
    session: 'signed in',
    check: 'a daily check',
    active_mode: 'an Active Mode session',
    live_session: 'a session rundown',
    exercise: 'an exercise',
    elina: 'a message to Elina',
    rom: 'a range-of-motion session',
    feedback: 'a Something wrong report',
    ticket: 'a support ticket',
    personal_note: 'a personal note from Brian',
    admin_note: 'an admin note',
  };
  return map[kind] ?? kind.replace(/_/g, ' ');
};

/** The biggest drop in a funnel window, or null when nobody was lost. */
export const biggestDrop = (w: FunnelWindow): FunnelStepCount | null =>
  w.steps.reduce<FunnelStepCount | null>((best, s) => (s.dropped !== null && s.dropped > 0 && (!best || s.dropped > (best.dropped ?? 0)) ? s : best), null);

/** Knowledge entries in display order, labelled. */
export const knowledgeRows = (elina: Record<string, string>): { key: string; label: string; value: string }[] => {
  const order = Object.keys(KNOWLEDGE_LABEL);
  return Object.entries(elina)
    .sort((a, b) => (order.indexOf(a[0]) === -1 ? 99 : order.indexOf(a[0])) - (order.indexOf(b[0]) === -1 ? 99 : order.indexOf(b[0])))
    .map(([key, value]) => ({ key, label: KNOWLEDGE_LABEL[key] ?? key.replace(/_/g, ' '), value: DOOR_LABEL[value] && key === 'door' ? DOOR_LABEL[value] : value.replace(/_/g, ' ') }));
};

/** The People table's two summary cells. */
export const elinaLine = (p: PersonRow): string => {
  const parts: string[] = [];
  if (p.elina.goal) parts.push(`goal ${p.elina.goal.replace(/_/g, ' ')}`);
  if (p.elina.door) parts.push(`door ${DOOR_LABEL[p.elina.door] ?? p.elina.door}`);
  if (p.elina.work_setting) parts.push(p.elina.work_setting.replace(/_/g, ' '));
  if (p.elina.hard_time) parts.push(`hard: ${p.elina.hard_time.replace(/_/g, ' ')}`);
  if (p.elina.symptoms) parts.push(p.elina.symptoms.replace(/_/g, ' '));
  return parts.length ? parts.join(' · ') : 'nothing yet';
};

export const asksLine = (p: PersonRow): string => {
  const parts: string[] = [];
  if (p.openTickets) parts.push(`${p.openTickets} open`);
  if (p.feedbackCount) parts.push(`${p.feedbackCount} feedback`);
  if (p.exerciseRequests) parts.push(`${p.exerciseRequests} exercise`);
  return parts.length ? parts.join(' · ') : '';
};

/* ── the phone card ─────────────────────────────────────────────────────── */

/**
 * Where a person is stuck, as one line for a card: the last step they
 * reached and the last thing the server saw them do. A paid person is not
 * stuck; the line says so rather than inventing a next step.
 */
export const stuckLine = (p: Pick<PersonRow, 'stage' | 'lastEventKind'>): string => {
  const did = `last did ${eventKindLabel(p.lastEventKind)}`;
  if (p.stage === 'paid') return `Paid · ${did}`;
  return `Stopped at ${STAGE_LABEL[p.stage]} · ${did}`;
};

/**
 * Time since the last event, in the unit that fits: "just now", "12 min ago",
 * "3 hours ago", "5 days ago"; "no event recorded" when the server has none.
 * Minutes are exact up to 90 minutes, then the hour and day words take over,
 * so a card never claims a precision the row does not carry.
 */
export const sinceLastEvent = (p: Pick<PersonRow, 'lastSeenAt'>, now: number = Date.now()): string => {
  if (!p.lastSeenAt) return 'no event recorded';
  const t = new Date(p.lastSeenAt).getTime();
  if (Number.isNaN(t)) return 'time not recorded';
  const min = Math.max(0, Math.round((now - t) / 60_000));
  if (min < 1) return 'just now';
  if (min <= 90) return `${min} min ago`;
  const h = Math.round(min / 60);
  if (h < 36) return `${h} ${h === 1 ? 'hour' : 'hours'} ago`;
  const d = Math.round(h / 24);
  return `${d} ${d === 1 ? 'day' : 'days'} ago`;
};

/* ── Where they stopped, why ────────────────────────────────────────────── */

/**
 * The fix the product itself sends after a failed first check (pv-auth
 * firstCheckFailureMessage.ts, ACTIVATION_FUNNEL_V1 section 8 rule 4). The
 * key is chosen the same way the server chooses it and the lines are the
 * server's, word for word, so the console shows Brian exactly what the
 * person was told. A reason with no fix of its own gets the generic line;
 * a person who never reached the camera gets no message at all, and that
 * is said rather than filled.
 */
export const FIX_KEYS = ['light', 'distance', 'app', 'connection', 'settle', 'hold', 'generic'] as const;
export type FixKey = (typeof FIX_KEYS)[number];
export const FIX_LINE: Record<FixKey, string> = {
  light: 'Face a window or turn on a lamp, and try again.',
  distance: "Sit back an arm's length so I can see your shoulders.",
  app: "Your phone's browser dropped the connection. The app holds it: get it from the App Store, then thirty seconds from your chair.",
  connection: 'The connection dropped. Try again from the same chair; it usually holds the second time.',
  settle: 'Sit back a little so I can see your shoulders, and let them settle level. Then thirty seconds.',
  hold: 'Prop the phone or hold it at eye height, elbows in, and try again.',
  generic: 'Something got in the way of your first check. Thirty seconds, same chair, and I tell you one thing.',
};
const LIGHT_STAGES = new Set(['light', 'too_dark', 'dark', 'lighting', 'low_light']);
const CLOSE_STAGES = new Set(['too_close', 'close', 'distance']);
const IPHONE_BROWSERS = new Set(['web_iphone', 'web_ipad']);
const norm = (v: unknown): string => (typeof v === 'string' ? v.trim().toLowerCase() : '');

/** The fix key for a failed attempt's reason, stage and platform; the server's failureMessageKeyFor. */
export const fixKeyForAttempt = (reason: string | null | undefined, stage: string | null | undefined, platform: string | null | undefined): FixKey => {
  const r = norm(reason);
  const s = norm(stage);
  const p = norm(platform);
  if (r === 'too_dark') return 'light';
  if (r === 'too_close') return 'distance';
  if (r === 'camera_gate_failed') return LIGHT_STAGES.has(s) ? 'light' : CLOSE_STAGES.has(s) ? 'distance' : 'generic';
  if (r === 'connection_failed') return IPHONE_BROWSERS.has(p) ? 'app' : 'connection';
  if (r === 'settle_stall') return 'settle';
  if (r === 'phone_moved' || r === 'camera_error') return 'hold';
  return 'generic';
};

/**
 * The fix for a stuck reason, or null when the product sends none for it.
 * A reason the client reported (stuckSource 'db', detail carries the
 * attempt's reason/stage/platform) is keyed exactly as the server keyed the
 * message it sent. A PostHog reason is mapped by what it carries: the gate's
 * block, the connection's platform, a camera_error cause. Reasons before the
 * camera (never opened, verification expired), a finished check with no
 * baseline, and "not measurable" have no failure message, because the
 * server only sends one after a failed attempt.
 */
export const fixFor = (p: Pick<PersonRow, 'stuckReason' | 'stuckDetail' | 'stuckSource'>): { key: FixKey; line: string } | null => {
  const d = p.stuckDetail ?? {};
  const reason = p.stuckReason;
  if (!reason) return null;
  if (p.stuckSource === 'db' && typeof d.reason === 'string' && d.reason !== 'unknown' && d.attempt_n !== undefined) {
    const key = fixKeyForAttempt(d.reason, typeof d.stage === 'string' ? d.stage : null, typeof d.platform === 'string' ? d.platform : null);
    return { key, line: FIX_LINE[key] };
  }
  let key: FixKey | null = null;
  switch (reason) {
    case 'camera_gate_failed': key = fixKeyForAttempt('camera_gate_failed', typeof d.block === 'string' ? d.block : null, null); break;
    case 'connection_failed': key = fixKeyForAttempt('connection_failed', null, typeof d.platform === 'string' ? d.platform : null); break;
    case 'settle_stall': key = 'settle'; break;
    case 'phone_moved': key = 'hold'; break;
    case 'never_started_camera': key = d.cause === 'camera_error' ? 'hold' : null; break;
    case 'camera_permission_denied':
    case 'abandoned_mid_beats': key = 'generic'; break;
    default: key = null;
  }
  return key ? { key, line: FIX_LINE[key] } : null;
};

/** Why the fix is absent, in words, for the reasons that get none. */
export const noFixWords = (reason: StuckReason | null): string => {
  switch (reason) {
    case 'never_opened_check': return 'No fix message applies: they never opened the check, so nothing failed. The first-hour nudge is the only send.';
    case 'never_started_camera': return 'No fix message applies: the camera never started, so the client reported no failed attempt.';
    case 'verification_expired': return 'No fix message applies: the verify link expired before the check. A fresh link is the fix.';
    case 'finished_no_baseline': return 'No fix message applies: the check finished but no baseline was stored. That is a product bug, not a person problem.';
    case 'unknown': return 'No fix message: the reason is not measurable, so the product did not guess one.';
    default: return 'No fix message on record for this reason.';
  }
};

/** Whether the stuck reason applies: it is read for people short of a first check, the way the funnel counts it. */
export const shortOfFirstCheck = (p: Pick<PersonRow, 'milestones'>): boolean => !p.milestones || p.milestones.first_check === null;

/**
 * The stuck reason in plain words for a row: the label when it was read,
 * the words for "not read" otherwise. Null for a person past the first
 * check, where the reason no longer describes where they are.
 */
export const stuckWords = (p: Pick<PersonRow, 'milestones' | 'stuckReason' | 'stuckReasonLabel' | 'stuckSource' | 'stuckUnavailableReason'>): { text: string; measured: boolean } | null => {
  if (!shortOfFirstCheck(p)) return null;
  if (p.stuckReason && (p.stuckSource === 'posthog' || p.stuckSource === 'db')) {
    return { text: p.stuckReasonLabel ?? STUCK_REASON_LABEL[p.stuckReason], measured: p.stuckReason !== 'unknown' };
  }
  if (p.stuckSource === 'unavailable') return { text: 'not measurable', measured: false };
  return { text: 'not read yet', measured: false };
};

/** Where the verdict came from, in words. */
export const stuckSourceWords = (p: Pick<PersonRow, 'stuckSource' | 'stuckBasis' | 'stuckUnavailableReason'>): string => {
  if (p.stuckSource === 'db') return p.stuckBasis?.event === 'FirstCheckAttempt:failed' ? "from the client's own attempt report" : 'from our own rows';
  if (p.stuckSource === 'posthog') return p.stuckBasis ? `from PostHog, ${p.stuckBasis.event.replace(/^\$/, '')}` : 'from PostHog';
  if (p.stuckSource === 'unavailable') return p.stuckUnavailableReason ?? 'PostHog could not be asked';
  return 'not fetched for this row';
};

/** "2 attempts, 1 failed" for a row; "" with none. Counts of rows the client reported. */
export const attemptsLine = (a: AttemptsSummary | null | undefined): string => {
  if (!a || a.count === 0) return '';
  const parts = [`${a.count} ${a.count === 1 ? 'attempt' : 'attempts'}`];
  if (a.failed) parts.push(`${a.failed} failed`);
  if (a.succeeded) parts.push(`${a.succeeded} succeeded`);
  return parts.join(', ');
};

/** An attempt's length in minutes to one decimal, or the words when the client sent no seconds. */
export const attemptMinutes = (seconds: number | null): string => {
  if (typeof seconds !== 'number' || !Number.isFinite(seconds)) return 'not measured';
  const m = Math.round(seconds / 6) / 10;
  return m < 0.1 && seconds > 0 ? 'under 0.1 min' : `${m.toFixed(1)} min`;
};

/** An attempt's platform as words. */
export const attemptPlatformLabel = (platform: string | null): string => (platform ? ATTEMPT_PLATFORM_LABEL[platform] ?? platform.replace(/_/g, ' ') : 'not reported');

/** The four leading milestones as dots: reached with the instant, or not. */
export const milestoneDots = (m: Milestones): { milestone: Milestone; label: string; at: string | null }[] =>
  DOT_MILESTONES.map((k) => ({ milestone: k, label: MILESTONE_LABEL[k], at: m?.[k] ?? null }));

/** The browser-side stuck filter over the page's rows: a reason is only matched when it was actually read. */
export const filterByStuck = <T extends Pick<PersonRow, 'milestones' | 'stuckReason' | 'stuckSource'>>(rows: T[], stuck: StuckReason | null | undefined): T[] =>
  stuck ? rows.filter((r) => shortOfFirstCheck(r) && r.stuckReason === stuck && (r.stuckSource === 'posthog' || r.stuckSource === 'db')) : rows;

const ASK_WORDS: Record<number, string> = { 1: 'once', 2: 'twice' };

/**
 * The member's moment as one line in their own words, or null when there is
 * nothing to say. A moment with words: "Their moment: With my morning coffee
 * (set 2026-10-24, web)". No moment but asked: "No moment yet (asked twice)".
 * No moment and never asked: null. Every field is optional; nothing here
 * invents a value the server did not send.
 */
export const momentLine = (m: MomentView | null | undefined): string | null => {
  const words = typeof m?.words === 'string' && m.words.trim() ? m.words.trim() : null;
  const key = typeof m?.moment === 'string' && m.moment.trim() ? m.moment.trim().replace(/_/g, ' ') : null;
  const said = words ?? key;
  if (said) {
    const t = typeof m?.setAt === 'string' ? new Date(m.setAt).getTime() : NaN;
    const set = Number.isNaN(t) ? null : `set ${localDayKey(t)}`;
    const device = typeof m?.device === 'string' && m.device.trim() ? m.device.trim() : null;
    const detail = [set, device].filter(Boolean).join(', ');
    return `Their moment: ${said}${detail ? ` (${detail})` : ''}`;
  }
  const n = typeof m?.askedCount === 'number' && Number.isFinite(m.askedCount) ? Math.floor(m.askedCount) : 0;
  if (n <= 0) return null;
  return `No moment yet (asked ${ASK_WORDS[n] ?? `${n} times`})`;
};
